import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { useToast } from "../../hooks/use-toast";
import { DownloadCloud, ExternalLink, Link } from "lucide-react";

interface ImportFilesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: (urls: string[]) => void;
}

const ImportFilesDialog = ({ open, onOpenChange, onImportComplete }: ImportFilesDialogProps) => {
  const [urls, setUrls] = useState<string[]>(['']);
  const [importing, setImporting] = useState(false);
  const { toast } = useToast();

  const handleUrlChange = (index: number, value: string) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
  };

  const addUrlField = () => {
    setUrls([...urls, '']);
  };

  const removeUrlField = (index: number) => {
    if (urls.length > 1) {
      const newUrls = urls.filter((_, i) => i !== index);
      setUrls(newUrls);
    }
  };

  const handleImport = async () => {
    const validUrls = urls.filter(url => url.trim() !== '');
    
    if (validUrls.length === 0) {
      toast({
        title: "No valid URLs",
        description: "Please enter at least one valid URL",
        variant: "destructive",
      });
      return;
    }

    setImporting(true);
    
    try {
      // Simulate import process
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      onImportComplete(validUrls);
      
      toast({
        title: "Import complete",
        description: `Successfully imported ${validUrls.length} file${validUrls.length > 1 ? 's' : ''}`,
      });
      
      setUrls(['']);
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Import failed",
        description: "There was an error importing your files",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import Files from URL</DialogTitle>
        </DialogHeader>
        
        <div className="grid w-full items-center gap-4">
          <div className="space-y-4">
            {urls.map((url, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="flex-1">
                  <Input
                    type="url"
                    placeholder="https://example.com/file.pdf"
                    value={url}
                    onChange={(e) => handleUrlChange(index, e.target.value)}
                  />
                </div>
                {urls.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeUrlField(index)}
                    className="h-8 w-8 p-0"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                  </Button>
                )}
              </div>
            ))}
            
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2"
              onClick={addUrlField}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
              Add Another URL
            </Button>
          </div>
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row sm:justify-between sm:space-x-2">
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button 
            type="button"
            onClick={handleImport}
            disabled={importing || urls.every(url => url.trim() === '')}
            className="gap-2"
          >
            {importing ? "Importing..." : "Import Files"}
            {!importing && <DownloadCloud className="h-4 w-4" />}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFilesDialog;
